import {
  type CampaignAnalyticsQuery,
  campaignAnalyticsResponseSchema,
  dashboardSummaryResponseSchema,
} from "@adpilot/shared";
import { type ReportingPeriod, reportingPeriodSearchParams } from "@/stores/reporting-period";
import { api } from "./api";
import type { ReportingTimeZone } from "./reporting-time-zone";

export async function fetchDashboardSummary(
  period: ReportingPeriod,
  timeZone: ReportingTimeZone,
  shopIds?: string[],
  // Child ASINs; scopes Campaign rows, KPIs and trend but never the AI cards.
  asins?: string[],
) {
  const res = await api
    .get("dashboard/summary", {
      searchParams: {
        ...reportingPeriodSearchParams(period, timeZone),
        // ADP-110: aggregate analytics scope; empty/omitted keeps the legacy
        // single-shop resolution via the X-Shop-Id header.
        ...(shopIds?.length ? { shopIds: shopIds.join(",") } : {}),
        ...(asins?.length ? { asins: asins.join(",") } : {}),
      },
    })
    .json<{ success: true; data: unknown }>();
  return dashboardSummaryResponseSchema.parse(res.data);
}

export async function fetchCampaignAnalytics(
  // ADP-110: shopIds optional at the wrapper level — Task 4 threads the real
  // selected set; callers without one stay on the single-shop path.
  params: Omit<CampaignAnalyticsQuery, "shopIds" | "asins" | "range" | "startDate" | "endDate"> & {
    range: ReportingPeriod;
    timeZone: ReportingTimeZone;
    shopIds?: string[];
    asins?: string[];
  },
  signal?: AbortSignal,
) {
  const res = await api
    .get("dashboard/campaign-analytics", {
      signal,
      searchParams: {
        ...reportingPeriodSearchParams(params.range, params.timeZone),
        granularity: params.granularity,
        ...(params.campaignId ? { campaignId: params.campaignId } : {}),
        delivery: params.delivery,
        search: params.search,
        policy: params.policy.join(","),
        run: params.run.join(","),
        ...(params.shopIds?.length ? { shopIds: params.shopIds.join(",") } : {}),
        ...(params.asins?.length ? { asins: params.asins.join(",") } : {}),
      },
    })
    .json<{ success: true; data: unknown }>();
  return campaignAnalyticsResponseSchema.parse(res.data);
}
